import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '#environments';
import {
    Remediation,
    RemediationSummary,
    RemediationCostSummary,
    UpdateRemediationItdFields,
    CreateRemediation,
    UpdateRemediation,
    RemediationCostCode,
    LinkCostCode,
    PaginatedResult,
    BulkUploadRemediationsResponse,
    GetRemediationBulkUploadJobStatusResponse,
    OverdueRemediation,
} from '#models';

export interface RemediationFilters {
    search?: string;
    status?: string | null;
    page?: number;
    pageSize?: number;
}

export interface OverdueRemediationFilters {
    search?: string;
    bucket?: string | null;
    page?: number;
    pageSize?: number;
}

// Must match the API's ARES.Application.Remediations.Commands.BulkUploadRemediations.RemediationCsvColumns
// exactly, so a file exported here can always be re-uploaded via Bulk Upload.
const REMEDIATION_CSV_HEADER = [
    'RemediationId',
    'Name',
    'Status',
    'SiteId',
    'LocusId',
    'CurrentErpPhase',
    'Address',
    'City',
    'State',
    'Latitude',
    'Longitude',
    'ChevronProjectManager',
    'KeySupplierProjectManager',
    'OriginalBudget',
    'ItdVowd',
    'ItdBookedActuals',
    'VendorName',
];

@Injectable({ providedIn: 'root' })
export class RemediationService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/remediations`;

    getAll(filters: RemediationFilters = {}): Observable<PaginatedResult<RemediationSummary>> {
        const params: Record<string, string> = {};
        if (filters.search) params['search'] = filters.search;
        if (filters.status) params['status'] = filters.status;
        if (filters.page !== undefined) params['page'] = String(filters.page);
        if (filters.pageSize !== undefined) params['pageSize'] = String(filters.pageSize);

        return this._http.get<PaginatedResult<RemediationSummary>>(this._base, { params });
    }

    getById(remediationId: string): Observable<Remediation> {
        return this._http.get<Remediation>(`${this._base}/${remediationId}`);
    }

    getOverdue(filters: OverdueRemediationFilters = {}): Observable<PaginatedResult<OverdueRemediation>> {
        const params: Record<string, string> = {};
        if (filters.search) params['search'] = filters.search;
        if (filters.bucket) params['bucket'] = filters.bucket;
        if (filters.page !== undefined) params['page'] = String(filters.page);
        if (filters.pageSize !== undefined) params['pageSize'] = String(filters.pageSize);

        return this._http.get<PaginatedResult<OverdueRemediation>>(
            `${environment.apiBaseUrl}/v1/overdue-remediations`,
            { params },
        );
    }

    getCostSummary(remediationId: string): Observable<RemediationCostSummary> {
        return this._http.get<RemediationCostSummary>(`${this._base}/${remediationId}/cost-summary`);
    }

    updateItdFields(remediationId: string, dto: UpdateRemediationItdFields): Observable<Remediation> {
        return this._http.patch<Remediation>(`${this._base}/${remediationId}/itd-fields`, dto);
    }

    create(dto: CreateRemediation): Observable<Remediation> {
        return this._http.post<Remediation>(this._base, dto);
    }

    update(remediationId: string, dto: UpdateRemediation): Observable<Remediation> {
        return this._http.put<Remediation>(`${this._base}/${remediationId}`, dto);
    }

    delete(remediationId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${remediationId}`);
    }

    getCostCodes(remediationId: string): Observable<RemediationCostCode[]> {
        return this._http.get<RemediationCostCode[]>(`${this._base}/${remediationId}/cost-codes`);
    }

    linkCostCode(remediationId: string, dto: LinkCostCode): Observable<RemediationCostCode> {
        return this._http.post<RemediationCostCode>(`${this._base}/${remediationId}/cost-codes`, dto);
    }

    unlinkCostCode(remediationId: string, remediationCostCodeId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${remediationId}/cost-codes/${remediationCostCodeId}`);
    }

    /**
     * Exports remediations matching the given Status/search filters as CSV. Fetches every
     * matching record in one call (page/pageSize omitted) via the same list endpoint used by the
     * grid, then builds the CSV client-side — mirrors the Asset Inventory export pattern rather
     * than a dedicated server-side export endpoint.
     */
    exportCsv(status?: string | null, search?: string): Observable<Blob> {
        return this.getAll({ status: status ?? undefined, search }).pipe(
            map(result => this._buildCsvBlob(result.items)),
        );
    }

    /**
     * Uploads a CSV file for async Remediation bulk upload (create/update) processing.
     * Returns 202 Accepted with a job ID.
     */
    uploadCsv(file: File): Observable<BulkUploadRemediationsResponse> {
        const formData = new FormData();
        formData.append('csvFile', file, file.name);
        return this._http.post<BulkUploadRemediationsResponse>(`${this._base}/bulk-upload`, formData);
    }

    /**
     * Get Remediation bulk upload job status.
     */
    getBulkUploadStatus(jobId: string): Observable<GetRemediationBulkUploadJobStatusResponse> {
        return this._http.get<GetRemediationBulkUploadJobStatusResponse>(`${this._base}/bulk-upload-jobs/${jobId}`);
    }

    /**
     * Downloads the error report JSON for a bulk upload job by streaming it through the API,
     * rather than requesting the (unsigned, non-routable) blob storage URL returned in the job
     * status payload.
     */
    downloadErrorReport(jobId: string): Observable<Blob> {
        return this._http.get(`${this._base}/bulk-upload-jobs/${jobId}/error-report`, { responseType: 'blob' });
    }

    /** Triggers a browser download of the given blob, saved under the given filename. */
    downloadBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    private _buildCsvBlob(remediations: RemediationSummary[]): Blob {
        const rows = remediations.map(r =>
            [
                r.remediationId,
                r.name,
                r.status,
                r.siteId ?? '',
                r.locusId ?? '',
                r.currentErpPhase ?? '',
                r.address ?? '',
                r.city ?? '',
                r.state ?? '',
                r.latitude ?? '',
                r.longitude ?? '',
                r.chevronProjectManagerName ?? '',
                r.keySupplierProjectManager ?? '',
                r.originalBudget ?? '',
                r.itdVowd ?? '',
                r.itdBookedActuals ?? '',
                r.vendorName ?? '',
            ]
                .map(value => this._escapeCsvValue(String(value)))
                .join(','),
        );
        const csvContent = [REMEDIATION_CSV_HEADER.join(','), ...rows].join('\n');
        return new Blob([csvContent], { type: 'text/csv' });
    }

    private _escapeCsvValue(value: string): string {
        if (/[",\n]/.test(value)) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    }
}
